import React from 'react';
import { 
  CreditCard, Plus, Edit3, Trash2, DollarSign, Calendar, 
  Gift, Users, UserPlus
} from 'lucide-react';
import Card from '../ui/Card';
import Table from '../ui/Table';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import FormField from '../ui/FormField';

const SubscriptionPlansTab = ({ 
  subscriptionPlans,
  users,
  loading,
  error,
  showAddPlan,
  setShowAddPlan,
  editingPlan,
  setEditingPlan,
  showAssignPlan,
  setShowAssignPlan,
  selectedUserForPlan,
  setSelectedUserForPlan,
  planFormData,
  setPlanFormData,
  handleCreatePlan,
  handleUpdatePlan,
  handleDeletePlan,
  handleAssignPlan
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatPrice = (price, currency = 'USD', currency_symbol = '$') => {
    if (currency === 'INR') {
      return `₹${price.toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const resetPlanForm = () => {
    setPlanFormData({
      name: '',
      plan_type: 'monthly',
      // Currency support (NEW)
      currency: 'INR',
      currency_symbol: '₹',
      // Credit allocation (ENHANCED)
      credits_per_month: '',
      credits_granted: '',
      price: '',
      description: '',
      // Visibility control (NEW)
      is_visible_to_users: true
    });
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      name: plan.name,
      plan_type: plan.plan_type,
      // Currency support (ENHANCED)
      currency: plan.currency || 'INR',
      currency_symbol: plan.currency_symbol || '₹',
      // Credit allocation (ENHANCED)
      credits_per_month: plan.credits_per_month ? plan.credits_per_month.toString() : '',
      credits_granted: plan.credits_granted ? plan.credits_granted.toString() : '',
      price: plan.price.toString(),
      description: plan.description || '',
      // Visibility control (ENHANCED)
      is_visible_to_users: plan.is_visible_to_users !== undefined ? plan.is_visible_to_users : true
    });
  };

  const handleClosePlanModal = () => {
    setShowAddPlan(false);
    setEditingPlan(null);
    resetPlanForm();
  };

  const handleCloseAssignModal = () => {
    setShowAssignPlan(false);
    setSelectedUserForPlan(null);
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    const planData = {
      name: planFormData.name,
      plan_type: planFormData.plan_type,
      // Currency support (NEW)
      currency: planFormData.currency,
      currency_symbol: planFormData.currency_symbol,
      price: parseFloat(planFormData.price),
      description: planFormData.description,
      // Visibility control (NEW)
      is_visible_to_users: planFormData.is_visible_to_users
    };

    // Add appropriate credit field based on plan type
    if (planFormData.plan_type === 'pay_as_you_go') {
      planData.credits_granted = parseInt(planFormData.credits_granted);
    } else {
      planData.credits_per_month = parseInt(planFormData.credits_per_month);
    }

    if (editingPlan) {
      await handleUpdatePlan(editingPlan.id, planData);
    } else {
      await handleCreatePlan(planData);
    }
    handleClosePlanModal();
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    
    // Fix: Create proper assignment payload object as expected by backend
    // Backend expects: { user_id: string, plan_id: string, assigned_by?: string }
    const assignmentData = {
      user_id: selectedUserForPlan, // selectedUserForPlan is already the user ID string
      plan_id: document.getElementById('plan-select').value, // Use direct access as originally intended
      assigned_by: "superadmin" // Set the admin who is assigning the plan
    };
    
    await handleAssignPlan(assignmentData);
    handleCloseAssignModal();
  };

  const getPlanTypeIcon = (plan_type) => {
    switch (plan_type) {
      case 'trial':
        return <Gift size={16} color="#f59e0b" />;
      case 'monthly':
        return <Calendar size={16} color="#3b82f6" />;
      case 'annual':
        return <CreditCard size={16} color="#10b981" />;
      case 'pay_as_you_go':
        return <DollarSign size={16} color="#8b5cf6" />;
      default:
        return <CreditCard size={16} color="#6b7280" />;
    }
  };

  const getPlanTypeBadgeColor = (plan_type) => {
    switch (plan_type) {
      case 'trial':
        return { bg: '#fef3c7', text: '#d97706', border: '#fed7aa' };
      case 'monthly':
        return { bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe' };
      case 'annual':
        return { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' };
      case 'pay_as_you_go':
        return { bg: '#f3e8ff', text: '#7c3aed', border: '#d8b4fe' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px',
        color: '#6b7280'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e5e7eb',
          borderTop: '3px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <span style={{ marginLeft: '12px', fontSize: '16px' }}>Loading subscription plans...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          color: '#dc2626'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Plans Grid */}
      <Card style={{ marginBottom: '32px' }}>
        <Card.Header>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Card.Title>Subscription Plans</Card.Title>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => setShowAssignPlan(true)}
                variant="secondary"
                icon={<UserPlus size={16} />}
              >
                Assign Plan
              </Button>
              <Button
                onClick={() => setShowAddPlan(true)}
                icon={<Plus size={16} />}
              >
                Add Plan
              </Button>
            </div>
          </div>
        </Card.Header>
        
        <Card.Content>
          {subscriptionPlans && subscriptionPlans.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px'
            }}>
              {subscriptionPlans.map((plan) => {
                const badgeColors = getPlanTypeBadgeColor(plan.plan_type);
                return (
                  <Card 
                    key={plan.id} 
                    style={{
                      border: '2px solid #e5e7eb',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#111827',
                          margin: 0,
                          marginBottom: '8px'
                        }}>
                          {plan.name}
                        </h3>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: badgeColors.bg,
                          color: badgeColors.text,
                          border: `1px solid ${badgeColors.border}`
                        }}>
                          {getPlanTypeIcon(plan.plan_type)}
                          {plan.plan_type.charAt(0).toUpperCase() + plan.plan_type.slice(1)}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={() => handleEditPlan(plan)}
                          style={{
                            padding: '6px',
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Edit plan"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan.id)}
                          style={{
                            padding: '6px',
                            backgroundColor: '#fecaca',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Delete plan"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '8px',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          fontSize: '28px',
                          fontWeight: '800',
                          color: '#111827'
                        }}>
                          {formatPrice(plan.price, plan.currency || 'USD', plan.currency_symbol || '$')}
                        </span>
                        <span style={{
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>
                          {plan.plan_type === 'pay_as_you_go' ? 
                            '(one-time)' : 
                            `/ ${plan.plan_type === 'annual' ? 'year' : plan.plan_type === 'monthly' ? 'month' : 'trial'}`
                          }
                        </span>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          padding: '4px',
                          borderRadius: '4px',
                          backgroundColor: '#e0e7ff'
                        }}>
                          <CreditCard size={14} color="#4f46e5" />
                        </div>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#4f46e5'
                        }}>
                          {plan.plan_type === 'pay_as_you_go' ? 
                            `${(plan.credits_granted || 0).toLocaleString()} credits` :
                            `${(plan.credits_per_month || 0).toLocaleString()} credits`
                          }
                        </span>
                        {plan.plan_type === 'annual' && (
                          <span style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            / month
                          </span>
                        )}
                        {plan.plan_type === 'pay_as_you_go' && (
                          <span style={{
                            fontSize: '12px',
                            color: '#8b5cf6',
                            fontWeight: '500'
                          }}>
                            (one-time)
                          </span>
                        )}
                      </div>

                      {/* Visibility Badge (NEW) */}
                      {plan.is_visible_to_users === false && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          border: '1px solid #fed7aa',
                          marginBottom: '8px'
                        }}>
                          🔒 Admin Only
                        </div>
                      )}
                      
                      
                      {plan.description && (
                        <p style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          margin: 0,
                          lineHeight: '1.5'
                        }}>
                          {plan.description}
                        </p>
                      )}
                    </div>

                    <div style={{
                      paddingTop: '16px',
                      borderTop: '1px solid #e5e7eb',
                      fontSize: '12px',
                      color: '#9ca3af'
                    }}>
                      Created: {formatDate(plan.created_at)}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: '#6b7280'
            }}>
              <CreditCard size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p style={{ fontSize: '16px', marginBottom: '16px' }}>No subscription plans found</p>
              <Button
                onClick={() => setShowAddPlan(true)}
                icon={<Plus size={16} />}
              >
                Create First Plan
              </Button>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Add/Edit Plan Modal */}
      <Modal
        isOpen={showAddPlan || editingPlan}
        onClose={handleClosePlanModal}
        title={editingPlan ? 'Edit Plan' : 'Add New Plan'}
        maxWidth="max-w-md"
      >
        <form onSubmit={handlePlanSubmit}>
          <FormField
            label="Plan Name"
            type="text"
            value={planFormData.name}
            onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
            required
            placeholder="e.g., Basic Plan"
          />
          
          <FormField
            label="Plan Type"
            type="select"
            value={planFormData.plan_type}
            onChange={(e) => setPlanFormData({ ...planFormData, plan_type: e.target.value })}
            options={[
              { value: 'trial', label: 'Trial' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'annual', label: 'Annual' },
              { value: 'pay_as_you_go', label: 'Pay-as-you-go' }
            ]}
            required
          />

          {/* Currency Support (NEW) */}
          <FormField
            label="Currency"
            type="select"
            value={planFormData.currency}
            onChange={(e) => {
              const currency = e.target.value;
              const currencySymbols = {
                'INR': '₹',
                'USD': '$',
                'EUR': '€',
                'GBP': '£'
              };
              setPlanFormData({ 
                ...planFormData, 
                currency: currency,
                currency_symbol: currencySymbols[currency] || currency
              });
            }}
            options={[
              { value: 'INR', label: '🇮🇳 Indian Rupee (INR)' },
              { value: 'USD', label: '🇺🇸 US Dollar (USD)' },
              { value: 'EUR', label: '🇪🇺 Euro (EUR)' },
              { value: 'GBP', label: '🇬🇧 British Pound (GBP)' }
            ]}
            required
          />
          
          {/* Conditional Credit Fields (ENHANCED) */}
          {planFormData.plan_type === 'pay_as_you_go' ? (
            <FormField
              label="Credits Granted (One-time)"
              type="number"
              value={planFormData.credits_granted}
              onChange={(e) => setPlanFormData({ ...planFormData, credits_granted: e.target.value })}
              required
              min="1"
              placeholder="e.g., 1000"
            />
          ) : (
            <FormField
              label="Credits per Month"
              type="number"
              value={planFormData.credits_per_month}
              onChange={(e) => setPlanFormData({ ...planFormData, credits_per_month: e.target.value })}
              required
              min="1"
              placeholder="e.g., 1000"
            />
          )}
          
          <FormField
            label={`Price (${planFormData.currency_symbol}${planFormData.currency})`}
            type="number"
            value={planFormData.price}
            onChange={(e) => setPlanFormData({ ...planFormData, price: e.target.value })}
            required
            min="0"
            step="0.01"
            placeholder={`e.g., ${planFormData.currency === 'INR' ? '500' : '29.99'}`}
          />

          {/* Visibility Control (NEW) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={planFormData.is_visible_to_users}
                onChange={(e) => setPlanFormData({ ...planFormData, is_visible_to_users: e.target.checked })}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#667eea'
                }}
              />
              <span style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Visible to Users
              </span>
            </label>
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              margin: '4px 0 0 26px'
            }}>
              {planFormData.is_visible_to_users ? 
                '👁️ Users can see and request/purchase this plan in their dashboard' :
                '🔒 Only admins can assign this plan (hidden from users)'
              }
            </p>
          </div>
          
          <FormField
            label="Description"
            type="textarea"
            value={planFormData.description}
            onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })}
            placeholder="Brief description of the plan"
          />
          
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            justifyContent: 'flex-end'
          }}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClosePlanModal}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Assign Plan Modal */}
      <Modal
        isOpen={showAssignPlan}
        onClose={handleCloseAssignModal}
        title="Assign Plan to User"
        maxWidth="max-w-md"
      >
        <form onSubmit={handleAssignSubmit}>
          <FormField
            label="Select User"
            type="select"
            value={selectedUserForPlan || ''}
            onChange={(e) => setSelectedUserForPlan(e.target.value)}
            options={[
              { value: '', label: 'Choose a user...' },
              ...users.map((user) => ({
                value: user.id,
                label: `${user.full_name} (${user.email})`
              }))
            ]}
            required
          />
          
          <FormField
            label="Select Plan"
            type="select"
            id="plan-select"
            options={[
              { value: '', label: 'Choose a plan...' },
              ...subscriptionPlans.map((plan) => ({
                value: plan.id,
                label: `${plan.name} - ${formatPrice(plan.price, plan.currency || 'USD', plan.currency_symbol || '$')} (${plan.plan_type})`
              }))
            ]}
            required
          />
          
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            justifyContent: 'flex-end'
          }}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseAssignModal}
            >
              Cancel
            </Button>
            <Button type="submit">
              Assign Plan
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SubscriptionPlansTab;